/**
 * Lo que se le manda al servidor al emitir una factura hecha a mano.
 *
 * Vive suelto y sin importar nada, como `coincidencia.js`, `importes.js` y
 * `destinatarios.js`: la pantalla importa `@/App` para el axios configurado, y
 * probar cuatro líneas no puede arrastrar la aplicación entera y el router.
 *
 * Lo que decide de verdad
 * -----------------------
 * Que los IMPORTES no viajan. Los calcula el servidor desde las líneas, con el
 * impuesto que le toque a ese cliente: un canario no lleva IVA y esta pantalla
 * no tiene por qué saberlo. Mandar el total desde aquí permitía guardar una
 * factura cuyo total no cuadraba con sus propias líneas — pasó al editar, y
 * por eso se quitó de allí también.
 */

/**
 * Una línea tal como la entiende el servidor: lo que describe QUÉ se factura,
 * sin importes calculados (los rehace él) y con el impuesto normalizado.
 *
 * `tax_rate` vacío viaja como null, no como "". Null es "el del cliente";
 * "" es un `float('')` que revienta en el servidor con un 500 sin mensaje.
 * Y 0 es 0: "esta línea no lleva impuesto", que es distinto de null.
 */
export function lineaParaElServidor(l) {
  const linea = l || {};
  const vacio = (v) => v === '' || v === null || v === undefined;
  return {
    product_id: linea.product_id || null,
    product_ref: linea.product_ref || null,
    product_name: linea.product_name || null,
    product_type: linea.product_type || null,
    description: linea.description || '',
    quantity: Number(linea.quantity) || 0,
    unit_price: Number(linea.unit_price) || 0,
    discount: Number(linea.discount) || 0,
    tax_rate: vacio(linea.tax_rate) ? null : Number(linea.tax_rate),
    tax_note: (linea.tax_note || '').trim() || null,
  };
}

/** El cuerpo del POST /invoices a partir del formulario. */
export function cuerpoDeFacturaNueva(formulario) {
  const f = formulario || {};
  return {
    customer_id: f.customer_id,
    line_items: (f.line_items || []).map(lineaParaElServidor),
    // Vacío es "no hay", no "cadena vacía": el servidor pone entonces el
    // vencimiento por defecto en vez de intentar leer una fecha que no existe.
    due_date: f.due_date || null,
    notes: f.notes || null,
    // Obligatoria. Viaja tal cual y el servidor la rechaza si no la reconoce:
    // `clave()` no puede fallar, lo que no entiende cae a "efectivo", y una
    // factura diciendo que se cobró en mano cuando no es verdad es peor que
    // un error. La pantalla no deja llegar hasta aquí sin elegirla.
    payment_method: f.payment_method || null,
    invoice_reference: f.invoice_reference || null,
    // Para el cliente: sale impresa. `notes` es interna y no sale nunca.
    observaciones: (f.observaciones || '').trim() || null,
    // De qué mes es el servicio, "AAAA-MM". Con él resuelve el servidor los
    // {mes}/{año} de las líneas. Vacío = el mes de emisión.
    periodo: f.periodo || null,
  };
}

const MESES_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio',
  'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

/** El mes de hoy, en el formato de <input type="month">: "2026-09". */
export function periodoActual(hoy = new Date()) {
  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Lo que va a salir impreso: {mes}, {año} y {mes_num} resueltos con el
 * periodo elegido. Es una VISTA PREVIA. Quien resuelve de verdad es el
 * servidor (`replace_date_variables`), al emitir, y esto tiene que decir
 * exactamente lo mismo: si la pantalla enseñara "Agosto" y el papel dijera
 * "Septiembre", nadie se fiaría de la pantalla.
 *
 * Con un periodo que no se entiende se devuelve el texto tal cual, con sus
 * llaves: mejor ver "{mes}" y darse cuenta que ver un mes inventado.
 */
export function resolverVariables(texto, periodo) {
  const t = texto || '';
  const m = /^(\d{4})-(\d{2})$/.exec(periodo || '');
  if (!m) return t;
  const año = m[1];
  const mesNum = m[2];
  const mes = MESES_ES[Number(mesNum) - 1];
  if (!mes) return t;
  return t.split('{mes_num}').join(mesNum)
    .split('{mes}').join(mes)
    .split('{año}').join(año);
}

/** Si el texto lleva alguna variable sin resolver. Para avisar en pantalla. */
export function llevaVariables(texto) {
  return /\{(mes|año|mes_num)\}/.test(texto || '');
}

/**
 * Por qué NO se le puede emitir una factura suelta a esta ficha.
 *
 * Recibe la respuesta de `/leads/{id}/billing-status?suelta=true`, que es el
 * mismo `billing_readiness` que el servidor aplicará al emitir. Aquí no se
 * repite ninguna regla: se traduce lo que ha contestado.
 *
 * Devuelve null cuando se puede, y TAMBIÉN cuando todavía no se sabe (no ha
 * contestado, o falló la consulta). Es deliberado: quien decide es el
 * servidor, que se niega con el motivo exacto. Esto solo evita teclear seis
 * líneas para que te lo digan al final — 329 de 2.037 clientes no pasan el
 * candado, así que pasa a menudo.
 */
export function motivoParaNoEmitir(estado) {
  if (!estado) return null;

  if (estado.es_cliente === false) {
    return `${estado.name || 'Esta ficha'} todavía no es cliente: es un lead. `
      + 'Solo se factura a clientes.';
  }
  if (estado.billing_ready === false) {
    const faltan = (estado.billing_missing || []).join(', ');
    return faltan
      ? `Faltan datos de facturación: ${faltan}.`
      : 'Faltan datos de facturación.';
  }
  return null;
}

/**
 * El texto con el que entra un producto en la línea: su nombre y, debajo, su
 * descripción del catálogo si la tiene.
 *
 * Angel, 21-sep-2026: "la descripción del producto déjame poner saltos de
 * línea y que no sea todo seguido, y variables como mes y año". Antes la
 * línea se quedaba solo con el nombre y la descripción del catálogo no
 * llegaba a la factura. Los saltos de línea se respetan tal cual: el PDF los
 * convierte en <br/> y la pantalla los pinta con `whitespace-pre-line`. Las
 * variables las resuelve el servidor al emitir, con el mes del servicio.
 */
export function descripcionDeLinea(producto) {
  const p = producto || {};
  const nombre = (p.name || '').trim();
  const detalle = (p.description || '').replace(/\r\n/g, '\n').trim();
  return detalle ? `${nombre}\n${detalle}` : nombre;
}

/**
 * Si el precio de esta línea se puede tocar en la factura.
 *
 * Solo los productos marcados "línea a medida" en el catálogo, igual que en
 * los presupuestos (Angel, 21-sep-2026: "esos productos que el precio es
 * modificable se pueda modificar también en la factura"). La marca se copia
 * a la línea al añadirla; para líneas guardadas antes de que se copiara, se
 * mira el catálogo.
 */
export function puedeCambiarPrecio(linea, catalogo) {
  if (!linea) return false;
  if (linea.allow_custom_line) return true;
  const producto = (catalogo || []).find((p) => p.id === linea.product_id);
  return Boolean(producto && producto.allow_custom_line);
}

/** Lo que se enseña antes de emitir, para que se pueda comprobar. */
export function totalDeLasLineas(lineas) {
  return (lineas || []).reduce((suma, l) => suma + (Number(l?.total) || 0), 0);
}

export default cuerpoDeFacturaNueva;
